import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, FileUp, Loader2, Lock, ShieldCheck, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] font-medium text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground/80">{hint}</p>}
  </div>
);

export default function DonorOnboarding() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [reqs, setReqs] = useState(null);
  const [uploaded, setUploaded] = useState({});
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "IN", donor_type: "individual", address: "",
    pan_or_tax_id: "", is_anonymous: false, declaration_name: "", notes: "", consents: {},
    portal_password: "",
  });

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/public/onboarding/${token}`);
      setInvite(res.data);
      setReqs(res.data.requirements);
      setForm((f) => ({ ...f, name: res.data.name || "", email: res.data.email || "",
        phone: res.data.phone || "", country: res.data.country || "IN",
        donor_type: res.data.donor_type || "individual" }));
      setUploaded(Object.fromEntries((res.data.documents || []).map((d) => [d.key, d.filename])));
    } catch (e) { setError(apiError(e)); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const changeCountry = async (code) => {
    setForm({ ...form, country: code });
    const res = await api.get(`/public/onboarding/${token}/requirements/${code}`);
    setReqs(res.data);
  };

  const upload = async (docKey, file) => {
    if (!file) return;
    setBusy(`up-${docKey}`);
    const body = new FormData();
    body.append("doc_key", docKey);
    body.append("file", file);
    try {
      const res = await api.post(`/public/onboarding/${token}/documents`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded({ ...uploaded, [docKey]: res.data.filename });
      toast.success(`${file.name} uploaded`);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const submit = async () => {
    setBusy("submit");
    try {
      await api.post(`/public/onboarding/${token}/submit`, form);
      setDone(true);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (error) {
    return (
      <Screen>
        <div className="panel mx-auto max-w-md p-8 text-center" data-testid="onboarding-error">
          <Lock className="mx-auto h-8 w-8 text-destructive" />
          <h1 className="mt-4 text-lg font-bold text-foreground">This link cannot be opened</h1>
          <p className="mt-2 text-[13px] text-muted-foreground">{error}</p>
        </div>
      </Screen>
    );
  }
  if (!invite) {
    return <Screen><div className="flex items-center gap-2 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Opening your secure form…</div></Screen>;
  }
  if (done) {
    return (
      <Screen>
        <div className="panel mx-auto max-w-md p-8 text-center" data-testid="onboarding-complete">
          <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-500" />
          <h1 className="mt-4 text-xl font-bold text-foreground">Thank you, {form.name.split(" ")[0]}</h1>
          <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
            Your profile and documents have been sent to {invite.organisation} for verification.
            You will hear from {invite.invited_by} once your donor record is active.
          </p>
        </div>
      </Screen>
    );
  }

  const missingConsent = (invite.consents || []).some((c) => c.required && !form.consents[c.key]);
  const missingDocs = invite.require_documents
    && (reqs?.documents || []).some((d) => d.required && !uploaded[d.key]);

  return (
    <Screen>
      <div className="mx-auto max-w-3xl space-y-5">
        <div className="panel p-6" data-testid="onboarding-header">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-gradient-to-br from-primary to-[hsl(var(--teal))]">
              <Sparkles className="h-4 w-4 text-white" />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">{invite.organisation}</div>
              <h1 className="font-display text-xl font-bold text-foreground">Complete your donor profile</h1>
            </div>
          </div>
          <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">
            {invite.message || `${invite.invited_by} has invited you to create a verified donor profile. `}
            We need your details, the identification documents required in your country of residence and
            your consent. This link is unique to you and expires on {invite.expires_on}.
          </p>
        </div>

        <div className="panel p-6" data-testid="onboarding-details">
          <h2 className="text-base font-semibold text-foreground md:text-lg">Your details</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <F label="Full name or legal entity">
              <Input value={form.name} data-testid="onboard-name-input"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-muted/40" />
            </F>
            <F label="Country of residence">
              <Select value={form.country} onValueChange={changeCountry}>
                <SelectTrigger data-testid="onboard-country-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                <SelectContent>{invite.countries.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>))}</SelectContent>
              </Select>
            </F>
            <F label="Email"><Input value={form.email} data-testid="onboard-email-input"
               onChange={(e) => setForm({ ...form, email: e.target.value })}
               className="bg-muted/40 font-mono text-[12px]" /></F>
            <F label="Mobile"><Input value={form.phone} data-testid="onboard-phone-input"
               onChange={(e) => setForm({ ...form, phone: e.target.value })}
               className="bg-muted/40 font-mono text-[12px]" /></F>
            <F label="Donor type">
              <Select value={form.donor_type} onValueChange={(v) => setForm({ ...form, donor_type: v })}>
                <SelectTrigger data-testid="onboard-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                <SelectContent>{["individual", "corporate", "foundation", "institutional"].map((x) => (
                  <SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
              </Select>
            </F>
            {(reqs?.fields || []).map((fl) => (
              <F key={fl.key} label={`${fl.label}${fl.required ? "" : " (optional)"}`}>
                <Input value={form[fl.key] || ""} placeholder={fl.placeholder || ""}
                       data-testid={`onboard-${fl.key}-input`}
                       onChange={(e) => setForm({ ...form, [fl.key]: e.target.value })}
                       className="bg-muted/40 font-mono text-[12px]" />
              </F>
            ))}
            <div className="sm:col-span-2">
              <F label="Address"><Textarea rows={2} value={form.address} data-testid="onboard-address-input"
                 onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-muted/40 text-[12px]" /></F>
            </div>
          </div>
        </div>

        <div className="panel p-6" data-testid="onboarding-documents">
          <h2 className="text-base font-semibold text-foreground md:text-lg">
            Identification required in {reqs?.label}
          </h2>
          <p className="mt-1 text-[12px] text-muted-foreground">
            PDF, JPG, PNG or WebP, up to 10 MB each. Documents are stored encrypted and only seen by
            our verification team.
          </p>
          <div className="mt-4 space-y-2.5">
            {(reqs?.documents || []).map((d) => (
              <div key={d.key} data-testid={`onboard-doc-${d.key}`}
                   className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-muted/20 p-3 transition-colors duration-200 hover:border-primary/40">
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-foreground">
                    {d.label} {d.required ? <span className="text-destructive">*</span>
                      : <span className="text-[10.5px] text-muted-foreground">optional</span>}
                  </div>
                  {d.note && <div className="text-[11px] text-muted-foreground">{d.note}</div>}
                </div>
                {uploaded[d.key] && (
                  <span className="flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400"
                        data-testid={`onboard-doc-done-${d.key}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> {uploaded[d.key]}
                  </span>
                )}
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-[11px] text-foreground transition-colors duration-150 hover:bg-muted">
                  {busy === `up-${d.key}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <FileUp className="h-3.5 w-3.5 text-primary" />}
                  {uploaded[d.key] ? "Replace" : "Upload"}
                  <input type="file" className="hidden" data-testid={`onboard-upload-${d.key}`}
                         accept=".pdf,.jpg,.jpeg,.png,.webp"
                         onChange={(e) => upload(d.key, e.target.files?.[0])} />
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="panel p-6" data-testid="onboarding-consent">
          <h2 className="text-base font-semibold text-foreground md:text-lg">Consent and declaration</h2>
          <div className="mt-4 space-y-3">
            {(invite.consents || []).map((c) => (
              <label key={c.key} className="flex cursor-pointer items-start gap-2.5"
                     data-testid={`onboard-consent-${c.key}`}>
                <Checkbox checked={Boolean(form.consents[c.key])}
                          onCheckedChange={(v) => setForm({ ...form,
                            consents: { ...form.consents, [c.key]: Boolean(v) } })}
                          data-testid={`onboard-consent-box-${c.key}`} className="mt-0.5" />
                <span className="text-[12.5px] leading-relaxed text-muted-foreground">
                  {c.label} {c.required && <span className="text-destructive">*</span>}
                </span>
              </label>
            ))}
            <F label="Type your full name to sign this declaration">
              <Input value={form.declaration_name} data-testid="onboard-signature-input"
                     onChange={(e) => setForm({ ...form, declaration_name: e.target.value })}
                     placeholder="Your full legal name" className="bg-muted/40" />
            </F>
            <F label="Create a portal password (optional, 10+ characters)"
               hint="Set one now and you can sign back in any time to see your giving history and download your tax receipts.">
              <Input type="password" value={form.portal_password} data-testid="onboard-password-input"
                     onChange={(e) => setForm({ ...form, portal_password: e.target.value })}
                     placeholder="••••••••••" className="bg-muted/40" />
            </F>
          </div>
          <Button onClick={submit} data-testid="onboard-submit-button"
                  disabled={busy === "submit" || !form.name.trim() || !form.declaration_name.trim()
                    || missingConsent || missingDocs}
                  className="mt-5 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "submit" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Upload className="mr-2 h-4 w-4" />} Submit my profile
          </Button>
          {(missingConsent || missingDocs) && (
            <p className="mt-2 text-center text-[11px] text-muted-foreground" data-testid="onboard-blocked-hint">
              {missingDocs ? "Please upload every required document. " : ""}
              {missingConsent ? "Please accept the required consent statements." : ""}
            </p>
          )}
          <p className="mt-3 flex items-center justify-center gap-1.5 text-[10.5px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3" /> Submitted over TLS · single-use link · audited on receipt
          </p>
        </div>
      </div>
    </Screen>
  );
}

const Screen = ({ children }) => (
  <div className="grid min-h-screen place-items-center bg-background px-4 py-10">{children}</div>
);
